import { type FC } from "react";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { Link } from "react-router-dom";
import type { PlainRunner } from "@/queries/runner-queries";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { Banner } from "@/components/flexkit/Banner";

export const EnvironmentBanner: FC<{ environment?: PlainEnvironment; runner?: PlainRunner }> = ({
    runner,
    environment,
}) => {
    if (!runner || !environment) {
        return;
    }

    const isLocalRunner = runner.kind === RunnerKind.LOCAL;
    const isRemoteRunner = !isLocalRunner;
    const runnerIsOffline = runner.status?.phase === RunnerPhase.INACTIVE;
    const runnerIsDisabled = runner.spec?.desiredPhase === RunnerPhase.INACTIVE;
    const runnerIsDegraded = runner.status?.phase === RunnerPhase.DEGRADED;

    if (isLocalRunner) {
        if (runnerIsDisabled) {
            return (
                <Banner
                    text="This environment is stopped because an administrator disabled local environments in your organization."
                    variant="danger"
                    data-testid="local-disabled-runner"
                />
            );
        }
        if (runnerIsOffline) {
            return (
                <Banner
                    text="Gitpod Desktop is offline. Please open the desktop app to use this environment."
                    variant="danger"
                    data-testid="local-stopped-runner"
                />
            );
        }
    }

    if (isRemoteRunner) {
        if (runnerIsOffline || runnerIsDegraded) {
            const text = (
                <span>
                    This environment is experiencing technical issues. Please check the{" "}
                    <Link to={`/settings/runners/${runner.runnerId}`} className="underline">
                        runner settings
                    </Link>
                    .
                </span>
            );
            return <Banner text={text} variant="danger" data-testid="remote-runner-issues" />;
        }
    }

    return;
};
